import { TPayout } from '../../../../../api/src';
import {
  Badge,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { FC } from 'react';
import {
  NoData,
  ScrollBarWithFade,
} from '@payler/ui-components';
import { TableSkeleton } from '../../components/TableSkeleton/TableSkeleton';
import { PaymentDatesCell } from './ReportDateCell';
import dayjs from 'dayjs';
import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { getTransactionStatusBadgeVariant } from '../../helpers/utils';
import { CardAndOrderIdCell } from './CardAndOrderIdCell';
import { AmountWithCurrencyCell } from './AmountWithCurrencyCell';
import { Images } from './Images';
import { TableFooter } from '../components/TableFooter';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert } from './Alert';
import { tableStyleConfig } from '../tableStyleConfig';
import { GroupRow } from './GroupRow';
import {
  usePayoutsQuery,
  usePayoutsQueryPagerProps,
} from '../../hooks/payouts/queries';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../hooks/use-toasts';

type TWithdrawalsTableProps = {
  bulkPayoutId?: string;
  bulkRefundId?: string;
  bulkCreatedAt?: string;
};

export const WithdrawalsTable: FC<TWithdrawalsTableProps> = ({
  bulkPayoutId,
  bulkRefundId,
  bulkCreatedAt,
}) => {
  const options = { bulkPayoutId, bulkRefundId, bulkCreatedAt };
  const props = usePayoutsQueryPagerProps('withdrawals', options);
  return (
    <>
      <TableWithData options={options} />
      <React.Suspense fallback={null}>
        <TableFooter paginationProps={props} />
      </React.Suspense>
    </>
  );
};

const TableWithData: FC<{ options: TWithdrawalsTableProps }> = ({ options }) => {
  const { status, data } = usePayoutsQuery('withdrawals', options);
  const toasts = useToasts();

  switch (status) {
    case 'pending':
      return <TableSkeleton />;
    case 'error':
      toasts.error();
      return <NoData />;
    case 'success':
    default: {
      const empty = data.items.length === 0;
      if (empty) {
        return <NoData />;
      }
      return (
        <ScrollBarWithFade>
          <TableContainer position="relative">
            <Table styleConfig={tableStyleConfig}>
              <Header />
              <Tbody>
                {Object.entries(data.grouped)
                  .sort(([a], [b]) => -a.localeCompare(b))
                  .map(([group, rows]) => (
                    <Rows key={group} group={group} payments={rows} />
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ScrollBarWithFade>
      );
    }
  }
};

const Rows = ({ group, payments }: { group: string; payments: TPayout[] }) => {
  const { language } = useMerchantCabinetConfig();
  return (
    <>
      <tr>
        <GroupRow colSpan={5}>
          {dayjs(group).locale(language).format('dddd DD MMMM')}
        </GroupRow>
      </tr>
      {payments.map((row) => (
        <Row data={row} key={row.id} />
      ))}
    </>
  );
};

const Row = ({ data }: { data: TPayout }) => {
  const { contractId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const returnUrl = location.pathname + location.search;
  const url = `/${contractId}/makecredit/${data.id}${location.search}${location.search ? '&' : '?'}returnUrl=${returnUrl}`;

  return (
    <Tr onClick={() => navigate(url)}>
      <Td>
        <PaymentDatesCell
          bottomDate={data.firstOperationDate}
          topDate={data.lastOperationDate}
        />
      </Td>
      <Td>
        <Badge variant={getTransactionStatusBadgeVariant(data.status)}>
          {data.transactionStatusDescription}
        </Badge>
      </Td>
      <Td>
        <HStack spacing={0.5} alignItems="center">
          <span>{data.bankCountryCode}</span>
          <Images payment={data} />
        </HStack>
      </Td>
      <Td>
        <CardAndOrderIdCell data={data} />
      </Td>
      <Td textAlign="right">
        <HStack alignItems="center" spacing={1} justifyContent="flex-end">
          <AmountWithCurrencyCell
            amount={data.amount ?? 0}
            currencyCode={data.currencyCode}
            amountOfRefunds={data.refundsAmount ?? 0}
          />
          <Alert data={data} />
        </HStack>
      </Td>
    </Tr>
  );
};

const Header = () => {
  const { t } = useTranslation(['tableHeader']);
  return (
    <Thead>
      <Tr>
        <Th>{t('tableHeader:period')}</Th>
        <Th>{t('tableHeader:status')}</Th>
        <Th>{t('tableHeader:code')}</Th>
        <Th textAlign="right">{t('tableHeader:cardAndOrderId')}</Th>
        <Th textAlign="right">{t('tableHeader:amount')}</Th>
      </Tr>
    </Thead>
  );
};

export default WithdrawalsTable;
